import React from 'react';
import PropTypes from 'prop-types';

import { CodeSnippet } from '$partials/global';
import Button from '$partials/ui/button';
import Logo from '$partials/icons/logo';
import Image from '$partials/media/image';
import { IconCrown, IconLaurel } from '$partials/icons';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */
/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const ModuleLargeStats = ({
    athleteAvatar,
    athleteName,
    athleteLocation,
    athleteDefaultSport,
    stravaLink,
    activityTitle,
    activitySubtitle,
    activityLocation,
    distance,
    elevationGain,
    time,
    kom,
    qom,
    lcl,
    mapEmbed
}) => {
    return (
        <div className='my-44 md:my-70 bg-white mx-10 sm:mx-20 md:-mx-20 flex flex-wrap flex-col-reverse sm:flex-row'>
            <div className='w-full sm:w-6/12 module-stats-embed-code min-h-[310px] md:min-h-[588px]'>
                <CodeSnippet html={mapEmbed} />
            </div>
            <div className='w-full sm:w-6/12 pl-30 pr-40 md:px-40 pt-20 pb-30 sm:pb-40 md:py-30 flex flex-wrap self-start'>
                <div className='flex w-full justify-between'>
                    {/* Athlete Details */}
                    <div className='flex flex-row'>
                        <div
                            className='bg-cover rounded-full w-[46px] h-[46px] mr-20'
                            style={{backgroundImage: `url(${athleteAvatar.src})`}}
                            title={athleteAvatar.title} />
                        <div className='flex flex-col'>
                            <p className='type-style-8'>
                                {athleteName}
                            </p>
                            <div className='flex items-center type-style-7 text-dark-grey'>
                                <div
                                    className='mr-5'
                                    style={{
                                        fontSize: '10px',
                                        width: `${(athleteDefaultSport?.icon?.width / athleteDefaultSport?.icon?.height)}em` ,
                                        maxWidth: '100%'
                                    }}>
                                    <Image
                                        {...athleteDefaultSport?.icon}
                                        className='grayscale brightness-50'
                                        isSVG={true}/>
                                </div>
                                {athleteLocation}
                            </div>
                        </div>
                    </div>

                    <Button
                        as='a'
                        className='hidden md:flex self-start items-center bg-orange text-white rounded type-style-7  px-20 py-15'
                        href={stravaLink}
                        renderIcon={<Logo />}>
                        View on
                    </Button>
                </div>

                {/* Activity details */}
                <div className='flex flex-col mt-20'>
                    <p className='type-style-3'>{activityTitle}</p>
                    {activitySubtitle && (
                        <p className='type-style-8'>
                            {activitySubtitle}
                        </p>
                    )}
                    <p className='hidden md:block mt-40 type-style-7 '>{activityLocation}</p>
                </div>

                {/* Activity stats */}
                <div className='flex flex-wrap w-full mt-30 md:mt-40'>
                    <div className='flex flex-col w-1/3 pb-15 md:pb-30 border-b border-black/10'>
                        <p className='type-style-7 '>Distance</p>
                        <p className='type-style-5'>{distance}</p>
                    </div>
                    <div className='flex flex-col w-1/3 pb-15 md:pb-30 border-b border-black/10'>
                        <p className='type-style-7 '>Elev Gain</p>
                        <p className='type-style-5'>{elevationGain}</p>
                    </div>
                    <div className='flex flex-col w-1/3 pb-15 md:pb-30 border-b border-black/10'>
                        <p className='type-style-7 '>Time</p>
                        <p className='type-style-5'>{time}</p>
                    </div>
                    <div className='flex flex-col w-1/3 pt-15 md:pt-30'>
                        <div className='flex'>
                            <p className='type-style-7  mr-5'>KOM</p>
                            <IconCrown/>
                        </div>
                        <p className='type-style-5'>{kom}</p>
                    </div>
                    <div className='flex flex-col w-1/3 pt-15 md:pt-30'>
                        <div className='flex'>
                            <p className='type-style-7  mr-5'>QOM</p>
                            <IconCrown/>
                        </div>
                        <p className='type-style-5'>{qom}</p>
                    </div>
                    <div className='flex flex-col w-1/3 pt-15 md:pt-30'>
                        <div className='flex'>
                            <p className='type-style-7  mr-5'>LCL</p>
                            <IconLaurel/>
                        </div>
                        <p className='type-style-5'>{lcl}</p>
                    </div>
                </div>

                <Button
                    as='a'
                    className='flex md:hidden self-start items-center bg-orange text-white rounded type-style-7 px-20 py-15 mt-20'
                    href={stravaLink}
                    renderIcon={<Logo/>}>
                    View on
                </Button>
            </div>
        </div>
    );

};

/* =========================================================================  */
/* Typing and export(s) */
/* =========================================================================  */

ModuleLargeStats.propTypes = {
    athleteAvatar: PropTypes.object,
    athleteName: PropTypes.string,
    athleteLocation: PropTypes.string,
    athleteDefaultSport: PropTypes.object,
    stravaLink: PropTypes.string,
    activityTitle: PropTypes.string,
    activitySubtitle: PropTypes.string,
    activityLocation: PropTypes.string,
    distance: PropTypes.string,
    elevationGain: PropTypes.string,
    time: PropTypes.string,
    kom: PropTypes.string,
    qom: PropTypes.string,
    lcl: PropTypes.string,
    mapEmbed: PropTypes.string,
};

export default ModuleLargeStats;
